
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  ViewEncapsulation,
  computed,
  forwardRef,
  inject,
  input,
  model,
  output
} from '@angular/core';
import { SelectionChangeEvent } from '../../domain/selectable.domain';
import type { Position, SelectorPosition } from '../checkbox/checkbox.component';
import type { InputSignal, ModelSignal, OutputEmitterRef, Signal } from '@angular/core';
export const TD_WB_RADIO = new InjectionToken<RadioComponent>('TD_WB_RADIO');
@Component({
  selector: 'td-wb-radio',
  templateUrl: 'radio.component.html',
  styleUrls: ['radio.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: TD_WB_RADIO, useExisting: forwardRef(() => RadioComponent) }
  ]
})
export class RadioComponent {
  readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  readonly radioId: InputSignal<string | undefined> = input<string | undefined>();
  readonly name: InputSignal<string | undefined> = input<string | undefined>();
  readonly value: InputSignal<unknown> = input<unknown>();
  readonly tabIndex: InputSignal<string | undefined> = input<string | undefined>();

  readonly labelPosition: InputSignal<Position | undefined> = input<Position | undefined>();
  readonly selectorPosition: InputSignal<SelectorPosition | undefined> = input<SelectorPosition | undefined>();

  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-label' });
  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-labelledby' });
  readonly radioIdFromGroup: ModelSignal<string | undefined> = model<string | undefined>();
  readonly nameFromGroup: ModelSignal<string | undefined> = model<string | undefined>();

  private static nextId: number = 0;
  private readonly generatedId: string = `td-wb-radio-${RadioComponent.nextId++}`;

  readonly finalRadioId: Signal<string> = computed(() => {
    return (this.radioIdFromGroup() ?? this.radioId() ?? this.generatedId);
  });

  readonly finalName: Signal<string> = computed((): string => {
    return this.nameFromGroup() ?? this.name() ?? this.finalRadioId() ?? '';
  });

  // State
  readonly disabled: ModelSignal<boolean> = model<boolean>(false);
  readonly checked: ModelSignal<boolean> = model<boolean>(false);

  // Output
  readonly onSelectionChange: OutputEmitterRef<SelectionChangeEvent<RadioComponent>> = output<SelectionChangeEvent<RadioComponent>>();

  // Derived
  readonly isSelectorCenter: Signal<boolean> = computed(() => { return this.selectorPosition() === 'center'; });
  readonly isLabelLeft: Signal<boolean> = computed(() => { return this.labelPosition() === 'left'; });

  setRadioIdFromGroup(id: string): void {
    if ((this.radioId() ?? this.radioIdFromGroup()) !== id) {
      this.radioIdFromGroup.set(id);
      this.cdr.markForCheck();
    }
  }

  setNameFromGroup(groupName: string): void {
    if ((this.name() ?? this.nameFromGroup()) !== groupName) {
      this.nameFromGroup.set(groupName);
      this.cdr.markForCheck();
    }
  }

  onInputClick(event: Event): void {
    event.stopPropagation();
  }

  onInputChange(event: Event): void {
    event.stopPropagation();
    if (this.disabled()) {
      return;
    }
    this.setCheckedState(true,true);
  }

  setCheckedFromGroup(newCheckedState: boolean): void {
    this.setCheckedState(newCheckedState, false);
  }

  markForCheck(): void {
    this.cdr.markForCheck();
  }

  private emitSelectionChangeEvent(fromView: boolean): void {
    this.onSelectionChange.emit(new SelectionChangeEvent(this, fromView));
  }

  private setCheckedState(newCheckedState: boolean, fromView: boolean): void {
    if (this.checked() === newCheckedState){
      return;
    }
    this.checked.set(newCheckedState);
    if (newCheckedState) {
      this.emitSelectionChangeEvent(fromView);
    }
    this.cdr.markForCheck();
  }
}
